<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="deep-gl" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ pageTitle }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="hasPermissions('field-tags-create')"
          class="mr-4 success"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="field-tags-table"
              height="500"
              name="field-tags-table"
              :headers="headers"
              :persistent-filters="false"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="serverDataOptions"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
            >
              <template
                v-for="(header, index) in headers"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <span
                  v-if="header.value === 'options'"
                  :key="index"
                  class="d-flex align-center justify-center flex-nowrap"
                >
                  <v-tooltip v-if="hasPermissions('field-tags-edit')" top color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="indigo"
                        v-bind="attrs"
                        v-on="on"
                        @click="handleMergeIntoCurrentClick(item.item)"
                      >
                        <v-icon dark>mdi-arrow-collapse-down</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("FIELD_TAGS.MERGE_INTO_CURRENT_TOOLTIP") }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="hasPermissions('field-tags-edit')" top color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="teal"
                        v-bind="attrs"
                        v-on="on"
                        @click="handleMergeCurrentIntoOtherClick(item.item)"
                      >
                        <v-icon dark>mdi-arrow-collapse-up</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("FIELD_TAGS.MERGE_CURRENT_INTO_OTHER_TOOLTIP") }}</span>
                  </v-tooltip>
                  <v-tooltip v-if="hasPermissions('field-tags-edit')" top color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="deep-purple"
                        v-bind="attrs"
                        v-on="on"
                        @click="handleUpdateParentChildrenClick(item.item)"
                      >
                        <v-icon dark>kt-menu__link-icon flaticon-map</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("FIELD_TAGS.UPDATE_PARENT_CHILDREN_TOOLTIP") }}</span>
                  </v-tooltip>
                  <v-btn
                    icon
                    color="indigo"
                    :to="{
                      name: 'field-tags-view',
                      params: {
                        fieldTagTypeId: $route.params.fieldTagTypeId,
                        tagId: item.item.id,
                      },
                    }"
                    @click="rememberTableRow(item.item.id)"
                  >
                    <v-icon dark>mdi-eye</v-icon>
                  </v-btn>
                </span>
                <span v-else-if="header.value === 'children_count'"
                :key="`clickable-${index}`"
                @click="showChildren(item.item)"
                style="cursor: pointer;"
                class="primary--text font-weight-medium"
                >
                {{ getChildrenDisplay(item.item, item.value) }}
                </span>
                <template v-else>
                  {{ item.value || "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
    <MergeFieldTagsDialog
      v-model="mergeDialog"
      :mode="mergeMode"
      :field-tag="activeFieldTag"
      :field-tag-type-id="currentFieldTagTypeId"
      @merged="handleMergeCompleted"
    />
    <UpdateParentChildrenDialog
      v-model="updateParentChildrenDialog"
      :field-tag="activeFieldTag"
      :field-tag-type-id="currentFieldTagTypeId"
      @updated="handleParentChildrenUpdated"
    />
    <ShowChildrenDialog
      v-model="showChildrenDialog"
      :field-tag="activeFieldTag"
    />
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import MergeFieldTagsDialog from "@/views/pages/fieldTags/partials/MergeFieldTagsDialog.vue";
import UpdateParentChildrenDialog from "@/views/pages/fieldTags/partials/UpdateParentChildrenDialog.vue";
import ShowChildrenDialog from "@/views/pages/fieldTags/partials/ShowChildrenDialog.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";
import { GET_FIELD_TAG_TYPES } from "@/store/fieldTagTypes.module";
import { GET_FIELD_TAGS } from "@/store/fieldTags.module";

export default {
  name: "ListFieldTags",
  components: {
    Table,
    MergeFieldTagsDialog,
    UpdateParentChildrenDialog,
    ShowChildrenDialog,
  },
  data() {
    return {
      tableName: "field-tags-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      mergeDialog: false,
      mergeMode: null,
      activeFieldTag: null,
      updateParentChildrenDialog: false,
      showChildrenDialog: false,
      pageTitle: this.$t("FIELD_TAGS.TAGS_TITLE"),
      tableItemsMethod: GET_FIELD_TAGS,
      headers: [
        { text: "#", value: "id", align: "center", width: "15%" },
        { text: this.$t("GENERAL.LABELS.NAME"), value: "value", align: "center", width: "35%" },
        {text: this.$t("FIELD_TAGS.PARENT"), value: "parent_name", align: "center", width: "15%"},
        {text: this.$t("FIELD_TAGS.CHILDREN"), value: "children_count", align: "center", width: "15%"},
        { text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"), value: "options", sortable: false, align: "center", width: "20%" },
      ],
    };
  },
  computed: {
    currentFieldTagTypeId() {
      return parseInt(this.$route.params.fieldTagTypeId, 10) || null;
    },
    serverDataOptions() {
      return {
        method: this.tableItemsMethod,
        params: {
          field_tag_type_id: this.currentFieldTagTypeId,
        },
      };
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(`table-state.${this.tableName}.blinkRowItemKey`, null);
    this.loadFieldTagTypes().finally(() => {
      this.userStatePrepared = true;
    });
  },
  watch: {
    "$route.params.fieldTagTypeId"() {
      this.loadFieldTagTypes();
      if (this.$refs["field-tags-table"]) {
        this.$refs["field-tags-table"].fetchServerData();
      }
    },
  },
  methods: {
    loadFieldTagTypes() {
      return this.$store
        .dispatch(GET_FIELD_TAG_TYPES, { itemsPerPage: 1000, sortBy: ["name"], sortDesc: [false] })
        .then((response) => {
          this.pageTitle = this.$t("FIELD_TAGS.TAGS_TITLE");

          const selectedType = (response.data || []).find(
            (item) => String(item.id) === String(this.$route.params.fieldTagTypeId)
          );
          if (selectedType) {
            this.pageTitle = `${this.$t("FIELD_TAGS.TAGS_TITLE")} - ${selectedType.name || selectedType.label || ""}`;
          }
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        });
    },
    handleCreateClick() {
      this.$router.push({ name: "field-tags-create", params: { fieldTagTypeId: this.$route.params.fieldTagTypeId } });
    },
    rememberTableRow(id) {
      userStateService.setState(`table-state.${this.tableName}.blinkRowItemKey`, id);
    },
    handleMergeIntoCurrentClick(fieldTag) {
      this.mergeMode = "into-current";
      this.activeFieldTag = fieldTag;
      this.mergeDialog = true;
    },
    handleMergeCurrentIntoOtherClick(fieldTag) {
      this.mergeMode = "current-into";
      this.activeFieldTag = fieldTag;
      this.mergeDialog = true;
    },
    handleUpdateParentChildrenClick(fieldTag) {
      this.activeFieldTag = fieldTag;
      this.updateParentChildrenDialog = true;
    },
    showChildren(fieldTag) {
      this.activeFieldTag = fieldTag;
      this.showChildrenDialog = true;
    },
    getChildrenDisplay(fieldTag, cellValue) {
      const children = fieldTag.children || fieldTag.child_tags || [];

      if (children.length === 1) {
        return children[0].value || children[0].name || fieldTag.child_name || cellValue || "-";
      }

      if (children.length > 1) {
        return children.length;
      }

      if (Array.isArray(fieldTag.children_names) && fieldTag.children_names.length === 1) {
        return fieldTag.children_names[0];
      }

      const count = Number(fieldTag.children_count ?? cellValue);
      if (count === 1) {
        return fieldTag.child_name || fieldTag.children_name || cellValue || "-";
      }

      return count || "-";
    },
    handleParentChildrenUpdated() {
      if (this.$refs["field-tags-table"]) {
        this.$refs["field-tags-table"].fetchServerData();
      }
    },
    handleMergeCompleted() {
      if (this.$refs["field-tags-table"]) {
        this.$refs["field-tags-table"].fetchServerData();
      }
    },
  },
};
</script>
